import { ColumnDefV2, ColumnDefV2Format } from '@blk/aw-bff-typescript-models';
import { Column } from '@blk/awt-aladdinwealthserver';
import { FirstDataRenderedEvent } from 'ag-grid-community';
import { isUndefined, keyBy } from 'lodash';
import memoizeOne from 'memoize-one';

export const UNABLE_TO_FIND_COLTAG: string = 'Unable to find colTag';
export const getMappedColumnDefs = memoizeOne((columnDefs: ColumnDefV2[]) => keyBy(columnDefs, 'colTag'));

export const getColumnDefByColTag = (columnDefs: ColumnDefV2[], colTag: string): ColumnDefV2 => {
  const mappedColumnDefs = getMappedColumnDefs(columnDefs);
  if (mappedColumnDefs[colTag]) {
    return mappedColumnDefs[colTag];
  }
  throw new Error(`${UNABLE_TO_FIND_COLTAG} ${colTag} in columnDefs. Please examine configuration.`);
};

export const getColumnDefByColTagNonBreaking = (columnDefs: ColumnDefV2[], colTag: string): ColumnDefV2 | null => {
  const mappedColumnDefs = getMappedColumnDefs(columnDefs);
  if (mappedColumnDefs[colTag]) {
    return mappedColumnDefs[colTag];
  }
  return null;
};

// get multi column defs in 1 go to save mapping over all column defs per call of getColumnDefByColTag
export const getColumnDefsByColTags = (
  columnDefs: ColumnDefV2[],
  colTags: string[],
  logError: boolean = true
): (ColumnDefV2 | null)[] => {
  const mappedColumnDefs = getMappedColumnDefs(columnDefs);
  return colTags.map((colTag) => {
    if (mappedColumnDefs[colTag]) {
      return mappedColumnDefs[colTag];
    } else if (logError) {
      console.error(`${UNABLE_TO_FIND_COLTAG} ${colTag} in columnDefs. Please examine configuration.`);
      return null;
    }
    return null;
  });
};

export const getMappedColumnDefsByPartialColTag = (columnDefs: ColumnDefV2[], partialColTag: string): ColumnDefV2[] =>
  columnDefs.reduce((colDefsForColTag: ColumnDefV2[], colDef: ColumnDefV2): ColumnDefV2[] => {
    if (colDef.colTag.includes(partialColTag)) {
      colDefsForColTag.push(colDef);
    }
    return colDefsForColTag;
  }, []);

export const getColumnGivenColTag = (columnDefs: ColumnDefV2[], colTag: string): Column => {
  const mappedColumnDefs = getMappedColumnDefs(columnDefs);
  if (mappedColumnDefs[colTag]) {
    const { title, aggregationMethod, weightBy } = mappedColumnDefs[colTag];
    return {
      colTag,
      title,
      aggregationMethod,
      weightBy,
    };
  }
  throw new Error(`${UNABLE_TO_FIND_COLTAG} ${colTag} in columnDefs. Please examine configuration.`);
};

/**
 * @description
 * the function creates a map between partial colTags (after the last dot) and columnDefs with the corresponding colTag
 * it uses regex to identify columnDef whose colTag matches partially
 * @param columnDefs configured columnDefs
 * @returns a map between partial colTag and columnDefs whose colTag matches partially
 */
export const getMappedColumnDefsByPartialColTagV2 = (
  columnDefs: ColumnDefV2[]
): { [partialColTag: string]: ColumnDefV2[] } =>
  columnDefs.reduce<{ [partialColTag: string]: ColumnDefV2[] }>((mapping, columnDef) => {
    const { colTag } = columnDef;
    const partialMatches = /^(.*?)\.[^.]*$/.exec(colTag);
    const partialColTag = partialMatches?.[1];
    // omit if no suitable match found
    if (!isUndefined(partialColTag)) {
      const matchedColDefs = mapping[partialColTag];
      if (matchedColDefs) {
        matchedColDefs.push(columnDef);
      } else {
        mapping[partialColTag] = [columnDef];
      }
    }
    return mapping;
  }, {});

export const autoSizeColumnsOnFirstRender = (event: FirstDataRenderedEvent, columnIds: string[]) => {
  if (!event.api.isAnimationFrameQueueEmpty()) {
    setTimeout(() => {
      autoSizeColumnsOnFirstRender(event, columnIds);
    }, 80);
  } else {
    setTimeout(() => {
      event.api.autoSizeColumns(columnIds);
    }, 0);
  }
};

export const NUMBER_FORMATS: ColumnDefV2Format[] = [
  'NUMERIC_WHOLE_NUMBER',
  'NUMERIC',
  'NUMERIC_INVALID_ZERO',
  'NUMERIC_SHORTHAND',
  'CURRENCY',
  'CURRENCY_WITH_SYMBOL',
  'CURRENCY_SHORTHAND_WITH_SYMBOL',
  'CURRENCY_WHOLE_NUMBER_WITH_SYMBOL',
  'MULTI_CURRENCY',
  'MULTI_CURRENCY_WITH_SYMBOL',
  'MULTI_CURRENCY_SHORTHAND',
  'MULTI_CURRENCY_SHORTHAND_WITH_SYMBOL',
  'MULTI_CURRENCY_WHOLE_NUMBER_WITH_SYMBOL',
  'PERCENTAGE',
  'PERCENTAGE_WITHOUT_SYMBOL',
  'DECIMAL_AS_PERCENTAGE',
  'DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL',
  'ONE_DECIMAL_AS_PERCENTAGE',
  'ONE_DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL',
  'FOUR_DECIMALS_AS_PERCENTAGE',
  'FOUR_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL',
  'NUMERIC_FOUR_DECIMALS',
  'NUMERIC_TWO_DECIMALS',
];

export const MULTI_CURRENCY_FORMATS: ColumnDefV2Format[] = [
  'MULTI_CURRENCY',
  'MULTI_CURRENCY_AUTO',
  'MULTI_CURRENCY_WITH_SYMBOL',
  'MULTI_CURRENCY_WITH_SYMBOL_POSTFIX',
  'MULTI_CURRENCY_SHORTHAND',
  'MULTI_CURRENCY_SHORTHAND_WITH_SYMBOL',
  'MULTI_CURRENCY_WHOLE_NUMBER_WITH_SYMBOL',
];

export const COLUMN_TYPE_MAP: Record<ColumnDefV2Format, ColumnType> = {
  NUMERIC_WHOLE_NUMBER: 'auxNumberColumn',
  NUMERIC: 'auxNumberColumn',
  NUMERIC_INVALID_ZERO: 'auxNumberColumn',
  NUMERIC_SHORTHAND: 'auxNumberColumn',
  NUMERIC_FOUR_DECIMALS: 'auxNumberColumn',
  NUMERIC_TWO_DECIMALS: 'auxNumberColumn',
  CURRENCY: 'auxNumberColumn',
  CURRENCY_AUTO: 'auxNumberColumn',
  CURRENCY_WITH_SYMBOL: 'auxNumberColumn',
  CURRENCY_WITH_SYMBOL_POSTFIX: 'auxNumberColumn',
  CURRENCY_SHORTHAND_WITH_SYMBOL: 'auxNumberColumn',
  CURRENCY_WHOLE_NUMBER_WITH_SYMBOL: 'auxNumberColumn',
  MULTI_CURRENCY: 'auxNumberColumn',
  MULTI_CURRENCY_AUTO: 'auxNumberColumn',
  MULTI_CURRENCY_WITH_SYMBOL: 'auxNumberColumn',
  MULTI_CURRENCY_WITH_SYMBOL_POSTFIX: 'auxNumberColumn',
  MULTI_CURRENCY_SHORTHAND: 'auxNumberColumn',
  MULTI_CURRENCY_SHORTHAND_WITH_SYMBOL: 'auxNumberColumn',
  MULTI_CURRENCY_WHOLE_NUMBER_WITH_SYMBOL: 'auxNumberColumn',
  EXPRESSION_BASED_CURRENCY_WITH_SYMBOL: 'auxNumberColumn',
  PERCENTAGE: 'auxNumberColumn',
  PERCENTAGE_WITHOUT_SYMBOL: 'auxNumberColumn',
  DECIMAL_AS_PERCENTAGE: 'auxNumberColumn',
  DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL: 'auxNumberColumn',
  ONE_DECIMAL_AS_PERCENTAGE: 'auxNumberColumn',
  ONE_DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL: 'auxNumberColumn',
  FOUR_DECIMALS_AS_PERCENTAGE: 'auxNumberColumn',
  FOUR_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL: 'auxNumberColumn',
  ZERO_DECIMALS_AS_PERCENTAGE: 'auxNumberColumn',
  ZERO_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL: 'auxNumberColumn',
  DATETIME_AS_ISO_STRING: 'dateTimeColumn',
  TIME_AS_ISO_STRING: 'dateTimeColumn',
  DATE_AS_ISO_STRING: 'dateTimeColumn',
  YEAR_MONTH_DAY: 'dateTimeColumn',
  MONTH_DAY_YEAR: 'dateTimeColumn',
  STRING_UPPERCASE: 'auxTextColumn',
  STRING_LOWERCASE: 'auxTextColumn',
  STRING_START_CASE: 'auxTextColumn',
  STRING_UPPER_FIRST: 'auxTextColumn',
  INTRADAY: 'auxTextColumn',
};

export const DATE_TIME_FORMATS: ColumnDefV2Format[] = [
  'YEAR_MONTH_DAY',
  'MONTH_DAY_YEAR',
  'TIME_AS_ISO_STRING',
  'DATETIME_AS_ISO_STRING',
  'DATE_AS_ISO_STRING',
];

export const STRING_FORMATS: ColumnDefV2Format[] = [
  'STRING_UPPERCASE',
  'STRING_LOWERCASE',
  'STRING_START_CASE',
  'STRING_UPPER_FIRST',
];

export type ColumnType = 'auxNumberColumn' | 'auxTextColumn' | 'auxCenterAlignColumn' | 'dateTimeColumn' | undefined;

export function getColumnType(columnDef?: Pick<ColumnDefV2, 'format'>): ColumnType {
  if (!columnDef?.format) {
    return;
  }
  if (DATE_TIME_FORMATS.includes(columnDef.format)) {
    return 'dateTimeColumn';
  }
  if (STRING_FORMATS.includes(columnDef.format)) {
    return 'auxTextColumn';
  }
  return NUMBER_FORMATS.includes(columnDef.format) ? 'auxNumberColumn' : undefined;
}
export const isMultiCurrencyColumn = (columnDefs: ColumnDefV2[], colTag: string) => {
  const columnDef = getColumnDefByColTagNonBreaking(columnDefs, colTag);
  if (!columnDef?.format) {
    return false;
  }
  return MULTI_CURRENCY_FORMATS.includes(columnDef.format);
};
